/* eslint-disable react/prop-types */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export const Modal = ({ isOpen, onClose, children, className = "max-w-md", label }) => {
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // ponytail: inline style scroll lock, no library needed
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  // Focus management: trap focus, move into dialog, restore on close
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedElementRef.current = document.activeElement;

    // Move focus into the dialog
    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = dialogRef.current?.querySelectorAll(focusableSelectors);
    const firstFocusable = focusableElements?.[0];

    if (firstFocusable) {
      firstFocusable.focus();
    } else if (dialogRef.current) {
      dialogRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      if (!dialogRef.current?.contains(document.activeElement)) return;

      const elements = Array.from(
        dialogRef.current.querySelectorAll(focusableSelectors)
      );

      if (elements.length === 0) return;

      const activeElement = document.activeElement;
      const focusedIndex = elements.indexOf(activeElement);

      if (e.shiftKey) {
        if (focusedIndex === 0) {
          e.preventDefault();
          elements[elements.length - 1].focus();
        }
      } else {
        if (focusedIndex === elements.length - 1) {
          e.preventDefault();
          elements[0].focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedElementRef.current?.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal: framer-motion transforms on ancestors break `position: fixed`.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col w-full max-h-[85vh] bg-white shadow-2xl rounded-2xl ${className}`}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute z-10 p-1 text-gray-500 bg-white rounded-full shadow top-3 right-3 hover:text-gray-700"
        >
          <X size={22} />
        </button>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
};
